export type StoragePath = string & { readonly __storagePath: unique symbol };
export type GalleryVariant = 'original' | 'display' | 'thumbnail';

export const STORAGE_IMAGE_PATHS = {
    SHOP_IMAGES: 'shops/{shopId}/images/{imageId}',
    SHOP_GALLERY: 'shops/{shopId}/gallery/{assetId}/{variant}',
    SHOP_COVER: 'shops/{shopId}/cover/{fileName}',
    SHOP_LOGO: 'shops/{shopId}/logo/{fileName}',
    SHOP_CLIENT_PROFILE: 'shops/{shopId}/clients/profile/{fileName}',
    SHOP_LOCATION: 'shops/{shopId}/location/{path}',
    SHOP_SEAT_MAP_BACKGROUND: 'shops/{shopId}/seatMaps/{mapId}/background',
    SHOP_PRODUCTS: 'shops/{shopId}/products/{productId}',
    SHOP_SERVICES: 'shops/{shopId}/services/{serviceId}',
    USER_PROFILE: 'users/{userId}/profile/{imageId}',
} as const;

export const STORAGE_MAX_BYTES = {
    shopImage: 5 * 1024 * 1024,
    gallery: 10 * 1024 * 1024,
    seatMap: 10 * 1024 * 1024,
    userProfile: 2 * 1024 * 1024,
} as const;

export const STORAGE_UPLOAD_POLICIES = {
    shopImage: { maxBytes: STORAGE_MAX_BYTES.shopImage, contentTypePrefix: 'image/' },
    gallery: { maxBytes: STORAGE_MAX_BYTES.gallery, contentTypePrefix: 'image/' },
    seatMap: { maxBytes: STORAGE_MAX_BYTES.seatMap, contentTypePrefix: 'image/' },
    userProfile: { maxBytes: STORAGE_MAX_BYTES.userProfile, contentTypePrefix: 'image/' },
    location: { maxBytes: 10 * 1024 * 1024, contentTypePrefix: 'application/' },
} as const;

function segment(value: string, name: string): string {
    if (!value || value === '.' || value === '..' || /[\\/]/.test(value)) {
        throw new Error(`Invalid storage ${name}`);
    }
    return value;
}

function path(value: string): StoragePath {
    return value as StoragePath;
}

export const storagePaths = {
    shopImage: (shopId: string, imageId: string) =>
        path(`shops/${segment(shopId, 'shop ID')}/images/${segment(imageId, 'image ID')}`),
    gallery: (shopId: string, assetId: string, variant: GalleryVariant) =>
        path(
            `shops/${segment(shopId, 'shop ID')}/gallery/${segment(assetId, 'asset ID')}/${segment(variant, 'gallery variant')}`,
        ),
    cover: (shopId: string, fileName: string) =>
        path(`shops/${segment(shopId, 'shop ID')}/cover/${segment(fileName, 'file name')}`),
    logo: (shopId: string, fileName: string) =>
        path(`shops/${segment(shopId, 'shop ID')}/logo/${segment(fileName, 'file name')}`),
    clientProfile: (shopId: string, fileName: string) =>
        path(
            `shops/${segment(shopId, 'shop ID')}/clients/profile/${segment(fileName, 'file name')}`,
        ),
    location: (shopId: string, fileName: string) =>
        path(`shops/${segment(shopId, 'shop ID')}/location/${segment(fileName, 'file name')}`),
    seatMapBackground: (shopId: string, mapId: string) =>
        path(`shops/${segment(shopId, 'shop ID')}/seatMaps/${segment(mapId, 'map ID')}/background`),
    userProfile: (userId: string, imageId: string) =>
        path(`users/${segment(userId, 'user ID')}/profile/${segment(imageId, 'image ID')}`),
};

export function getStorageUploadPolicy(storagePath: StoragePath) {
    if (/^shops\/[^/]+\/gallery\//.test(storagePath)) return STORAGE_UPLOAD_POLICIES.gallery;
    if (/^shops\/[^/]+\/seatMaps\//.test(storagePath)) return STORAGE_UPLOAD_POLICIES.seatMap;
    if (/^users\/[^/]+\/profile\//.test(storagePath)) return STORAGE_UPLOAD_POLICIES.userProfile;
    if (/^shops\/[^/]+\/location\//.test(storagePath)) return STORAGE_UPLOAD_POLICIES.location;
    if (/^shops\/[^/]+\/(images|cover|logo|clients\/profile)\//.test(storagePath)) {
        return STORAGE_UPLOAD_POLICIES.shopImage;
    }
    throw new Error('Unsupported storage path');
}